import { useEffect, useRef, type RefObject } from "react";
import { isNearBottom, scrollToBottom } from "../lib/scrollFollow";

// 会話スレッドの自動スクロール (spec: docs/superpowers/specs/2026-07-27-thread-auto-scroll-design.md)。
// 「最下部付近にいるときだけ追従する」の固定状態 (pinned) を保持する React 配線。
// 判定と DOM 操作は src/lib/scrollFollow.ts に置き、この hook は状態と繋ぎだけを持つ。

export type FollowBottom = {
  /** スクロール領域 (overflow-y-auto の div) に付ける ref */
  containerRef: RefObject<HTMLDivElement | null>;
  /** 同じ div の onScroll に付ける。ユーザーの現在位置から pinned を更新する */
  onScroll: () => void;
  /** 追従を再開する。次に deps が変われば最下部へ送る (spec D5: per-Q 修正) */
  pin: () => void;
  /** 追従を止める。ユーザーの意図で別の場所を見せたいとき (spec D5: Review の「修正する」) */
  unpin: () => void;
};

/**
 * deps が変わるたび、最下部付近にいれば最下部へ追従する。
 *
 * 追従の可否を deps 変化後 (= 新しいバブルが DOM に入った後) に測ると、背の高い
 * バブルが 1 つ増えただけで「ユーザーが上にいる」と誤判定する。そのため判定は
 * onScroll 側で行い、ここでは前回の判定結果 (pinned) を読むだけにする (spec §3.3)。
 */
export function useFollowBottom(deps: readonly unknown[]): FollowBottom {
  const containerRef = useRef<HTMLDivElement | null>(null);
  // 会話は最下部から始まるので初期値は true。
  const pinnedRef = useRef(true);

  const onScroll = () => {
    const el = containerRef.current;
    if (el) pinnedRef.current = isNearBottom(el);
  };

  useEffect(() => {
    if (pinnedRef.current) scrollToBottom(containerRef.current);
    // deps は呼び出し側が「スレッドの中身が増える契機」として渡す (spec §3.2)。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return {
    containerRef,
    onScroll,
    pin: () => {
      pinnedRef.current = true;
    },
    unpin: () => {
      pinnedRef.current = false;
    },
  };
}
